import z from "zod";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import { sureApiCall } from "../axiosClient/sureApi";
import { zodParseWithLog } from "../apiErrorLogs";
import { fetchAllHydraMembers } from "../_common/hydra/fetchAllHydraMembers";

/**
 * Hook for the elementary packaging materials catalogue exposed by sure-api
 * (`PackagingReference`). Mirrors {@link useConsumptionReference}.
 *
 * Used by the "Create element" packaging form (Page1) as the source of the
 * type-of-packaging dropdown. Each row carries an optional `defaultWeight`
 * the form pre-fills as a hint and an optional `ssqId` the SsqRetriever
 * uses on the API side to attach a per-type SSQ.
 *
 * Localisation contract: sure-api uses Gedmo Translatable on
 * `PackagingReference.name`. The axios client injects `Accept-Language`
 * from `i18next.language`, so the API already returns `name` in the user's
 * locale (or the English default when no translation exists). The hook
 * therefore consumes a single `name` field instead of projecting between
 * `nameFr` / `nameEn` itself.
 */
export const PackagingReferenceApiSchema = z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    defaultWeight: z.number().nullable().optional(),
    ecobalyseProcessUuid: z.string().nullable().optional(),
});

export type PackagingReferenceApi = z.infer<typeof PackagingReferenceApiSchema>;

export type PackagingReference = {
    id: number;
    code: string;
    label: string;
    defaultWeight: number | null;
    ecobalyseProcessUuid: string | null;
};

/**
 * Pure projection from the API payload to the shape consumers rely on.
 * Falls back to `code` when the localised `name` is empty or whitespace —
 * a safety net for partially-translated catalogues.
 */
export const projectPackagingReference = (item: PackagingReferenceApi): PackagingReference => ({
    id: item.id,
    code: item.code,
    label: item.name.trim() !== "" ? item.name : item.code,
    defaultWeight: item.defaultWeight ?? null,
    ecobalyseProcessUuid: item.ecobalyseProcessUuid ?? null,
});

export const usePackagingReference = () => {
    const { i18n } = useTranslation();
    // Locale is part of the cache key — switching languages must
    // invalidate the cache because sure-api returns localised `name`s
    // based on the `Accept-Language` header set by the axios interceptor.
    const lang = i18n.language?.startsWith("fr") ? "fr" : "en";

    const query = useQuery({
        cacheTime: 0,
        staleTime: 0,
        queryKey: ["allPackagingReferences", lang],
        queryFn: async () => {
            return await fetchAllHydraMembers({
                client: sureApiCall,
                endpoint: "/api/packaging_material_references",
                itemsPerPage: 100,
                requestConfig: { headers: { "Accept-Language": lang } },
            });
        },
        select: (items) => {
            const data = zodParseWithLog(items, PackagingReferenceApiSchema.array(), "allPackagingReferences");

            return data.map(projectPackagingReference);
        },
    });

    return {
        packagingReferences: query.data ?? [],
        ...query,
    };
};
